import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { client, urlFor } from '../client'; // Import Sanity client
import { PortableText } from '@portabletext/react';

const AboutPage = () => {
  const [activeTab, setActiveTab] = useState('about');
  const [aboutPageData, setAboutPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define tabs statically for now, labels could potentially come from Sanity too
  const tabs = [
    { id: 'about', label: 'About Us' },
    { id: 'instructional', label: 'Instructional Focus' },
    { id: 'grading', label: 'Grading Policy' },
    { id: 'promotional', label: 'Promotional Policy' },
    { id: 'accountability', label: 'Accountability/Performance Reports' }
  ];

  // Hardcoded links for accountability reports
  const reportLinks = [
    'https://www.schools.nyc.gov/schools/R052', // Placeholder/example link for snapshot
    '/documents/doe-school-quality-snapshot.pdf', // Assuming these are in public/documents
    '/documents/doe-quality-review-report.pdf',
    '/documents/doe-arts-in-schools-report.pdf'
  ];

  // Define components for PortableText, especially for handling links
  const ptComponents = {
    marks: {
      link: ({children, value}) => {
        if (!value?.href) {
          return <span>{children}</span>;
        }
        // Automatically add rel="noopener noreferrer" for external links
        const rel = !value.href.startsWith('/') ? 'noreferrer noopener' : undefined;
        // Open in new tab if blank is true or if it's an absolute URL
        const target = (value.blank || value.href.startsWith('http')) ? '_blank' : undefined;
        return (
          <a href={value.href} className="text-[#691e31] hover:underline" rel={rel} target={target}>
            {children}
          </a>
        );
      }
    },
    // You can add custom components for block types (e.g., paragraphs) if needed
    // block: {
    //   normal: ({children}) => <p className="text-gray-700 leading-relaxed mb-4">{children}</p>,
    // },
  };

  useEffect(() => {
    const query = `*[_type == "aboutPage"][0]{ 
      ...,
      heroImage{asset->{url}},
      visionImage{asset->{url}},
      missionImage{asset->{url}},
      instructionalImage{asset->{url}},
      gradingRubricImage{asset->{url}},
      promotionalPolicyImage{asset->{url}},
      accountabilityReports[]{ // Fetch the array of report objects
        _key,
        label,
        url
      }
    }`;

    client.fetch(query)
      .then((data) => {
        setAboutPageData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching About Page data:', err);
        setError('Failed to load content.');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen">Error: {error}</div>;
  }

  if (!aboutPageData) {
    return <div className="flex justify-center items-center h-screen">No page data found.</div>;
  }

  // // Log the accountability reports data to inspect it
  // console.log("Accountability Reports Data:", aboutPageData.accountabilityReports);

  const heroImageUrl = aboutPageData.heroImage?.asset?.url;
  const visionImageUrl = aboutPageData.visionImage?.asset?.url;
  const missionImageUrl = aboutPageData.missionImage?.asset?.url;
  const instructionalImageUrl = aboutPageData.instructionalImage?.asset?.url;
  const gradingRubricImageUrl = aboutPageData.gradingRubricImage?.asset?.url;
  const promotionalPolicyImageUrl = aboutPageData.promotionalPolicyImage?.asset?.url;

  return (
    <div className="bg-white">
      {/* Hero Banner - Use Sanity Data */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {heroImageUrl ? (
            <img 
              src={heroImageUrl}
              alt={aboutPageData.heroTitle || 'P.S. 052 School'}
              className="absolute inset-0 w-full h-full object-cover object-center" 
            />
          ) : (
            <div className="absolute inset-0 w-full h-full object-cover object-center bg-gray-300"></div> // Placeholder
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        {/* Content overlay */}
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {aboutPageData.heroTitle} {/* Use Sanity Title */}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Navigation (static labels) */}
      <section className="bg-gray-50 py-4 sticky top-0 z-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2 md:gap-4 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#691e31] text-white shadow-md'
                    : 'bg-white text-[#2a4865] hover:bg-gray-100'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Tab Content Section - Use Sanity Data */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {/* About Us Tab */}
          {activeTab === 'about' && (
            <div>
              <div className="flex justify-between items-center mb-10">
                <h2 className="text-[#2a4865] text-3xl font-bold relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                  {aboutPageData.aboutTabTitle} {/* Sanity Title */}
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Vision Card */}
                <div className="flex flex-col h-full">
                  <div className="h-64 bg-gray-200 mb-6 relative overflow-hidden rounded-lg shadow-md">
                    {visionImageUrl ? (
                      <img 
                        src={visionImageUrl}
                        alt={aboutPageData.visionCardSubtitle || 'School Vision'}
                        className="absolute inset-0 w-full h-full object-fill"
                      />
                    ) : (
                      <div className="absolute inset-0 w-full h-full object-cover bg-gray-300"></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                      <div className="p-6">
                        <div className="flex items-center">
                          <div className="w-1 h-8 bg-yellow-500 mr-3"></div>
                          <h3 className="text-white text-xl font-semibold">
                            {aboutPageData.visionCardTitle} {/* Sanity Title */}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg shadow-sm flex-1">
                    <h3 className="text-xl font-bold text-[#691e31] mb-4 relative overflow-hidden pb-2">
                      {aboutPageData.visionCardSubtitle} {/* Sanity Subtitle */}
                      <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-yellow-500"></span>
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {aboutPageData.visionText} {/* Sanity Text */}
                    </p>
                  </div>
                </div>
                
                {/* Mission Card */}
                <div className="flex flex-col h-full">
                  <div className="h-64 bg-gray-200 mb-6 relative overflow-hidden rounded-lg shadow-md">
                     {missionImageUrl ? (
                      <img 
                        src={missionImageUrl}
                        alt={aboutPageData.missionCardSubtitle || 'School Mission'}
                        className="absolute inset-0 w-full h-full object-fill"
                      />
                     ) : (
                       <div className="absolute inset-0 w-full h-full object-cover bg-gray-300"></div>
                     )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                      <div className="p-6">
                        <div className="flex items-center">
                          <div className="w-1 h-8 bg-yellow-500 mr-3"></div>
                          <h3 className="text-white text-xl font-semibold">
                            {aboutPageData.missionCardTitle} {/* Sanity Title */}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg shadow-sm flex-1">
                    <h3 className="text-xl font-bold text-[#691e31] mb-4 relative overflow-hidden pb-2">
                      {aboutPageData.missionCardSubtitle} {/* Sanity Subtitle */}
                      <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-yellow-500"></span>
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {aboutPageData.missionText} {/* Sanity Text */}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Instructional Focus Tab */}
          {activeTab === 'instructional' && (
            <div>
              <div className="flex justify-between items-center mb-10">
                <h2 className="text-[#2a4865] text-3xl font-bold relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                  {aboutPageData.instructionalTabTitle} {/* Sanity Title */}
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                <div className="md:col-span-5">
                  <div className="h-full bg-gray-200 relative overflow-hidden rounded-lg shadow-md min-h-[300px]">
                    {instructionalImageUrl ? (
                      <img 
                        src={instructionalImageUrl}
                        alt={aboutPageData.instructionalTabTitle || 'Instructional Focus'} 
                        className="absolute inset-0 w-full h-full object-fill"
                      />
                    ) : (
                       <div className="absolute inset-0 w-full h-full object-cover bg-gray-300"></div>
                    )}
                  </div>
                </div>
                
                <div className="md:col-span-7">
                  <div className="bg-white p-8 rounded-lg shadow-sm h-full">
                    <div className="w-12 h-1 bg-yellow-500 mb-6"></div>
                    <p className="text-gray-700 text-lg leading-relaxed">
                      {aboutPageData.instructionalText} {/* Sanity Text */}
                    </p>
                    
                    {/* Key Points from Sanity */}
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                      {aboutPageData.instructionalKeyPoints?.map((point) => (
                        <div key={point._key} className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#691e31]">
                          <h4 className="font-medium text-[#2a4865]">{point.title}</h4>
                          <p className="text-sm text-gray-600">{point.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Grading Policy Tab */}
          {activeTab === 'grading' && (
            <div>
              <div className="flex justify-between items-center mb-10">
                <h2 className="text-[#2a4865] text-3xl font-bold relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                  {aboutPageData.gradingTabTitle} {/* Sanity Title */}
                </h2>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8">
                  <div className="bg-white p-8 rounded-lg shadow-sm">
                    <h3 className="text-xl font-bold text-[#691e31] mb-4 relative overflow-hidden pb-2">
                      {aboutPageData.gradingPolicyTitle} {/* Sanity Title */}
                      <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-yellow-500"></span>
                    </h3>
                    
                    {/* Render gradingPolicyText using PortableText */}
                    {aboutPageData.gradingPolicyText && (
                      <div className="text-gray-700 leading-relaxed mb-8 space-y-4">
                        <PortableText 
                          value={aboutPageData.gradingPolicyText} 
                          components={ptComponents} 
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="lg:col-span-4">
                  <div className="bg-[#2a4865] text-white p-6 rounded-lg shadow-sm h-full">
                    <div className="flex items-center mb-6 bg-yellow-500 p-3 rounded-t-lg -mt-6 -mx-6 px-6">
                      <h3 className="text-xl font-bold text-[#2a4865]">
                        {aboutPageData.gradingRubricTitle} {/* Sanity Title */}
                      </h3>
                    </div>
                    
                    {/* Rubric Image from Sanity */}
                    <div className="relative h-48 bg-white/10 rounded-lg mb-4 overflow-hidden">
                      {gradingRubricImageUrl ? (
                        <img 
                          src={gradingRubricImageUrl} 
                          alt={aboutPageData.gradingRubricTitle || "Grading Rubric"} 
                          className="absolute inset-0 w-full h-full object-fill" // Use contain for rubric image
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <p className="text-white text-center p-4">
                            Grading rubric image not available.
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Rubric Levels from Sanity */}
                    {aboutPageData.gradingRubricLevels?.map((level) => (
                       <div key={level._key} className="flex justify-between items-center text-white/90 px-2 py-3 border-b border-white/20 last:border-b-0">
                        <span>{level.label}</span>
                        <span className="font-bold">{level.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Promotional Policy Tab */}
          {activeTab === 'promotional' && (
            <div>
              <div className="flex justify-between items-center mb-10">
                <h2 className="text-[#2a4865] text-3xl font-bold relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                  {aboutPageData.promotionalTabTitle}
                </h2>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                <div className="lg:col-span-2">
                  <div className="bg-white p-8 rounded-lg shadow-sm">
                    <h3 className="text-xl font-bold text-[#691e31] mb-4 relative overflow-hidden pb-2">
                      {aboutPageData.promotionalPolicyTitle}
                      <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-yellow-500"></span>
                    </h3>
                    {aboutPageData.promotionalPolicyText?.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="text-gray-700 mb-6 last:mb-0 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
                
                <div className="lg:col-span-1">
                  <div className="h-[250px] lg:h-[300px] bg-gray-200 relative overflow-hidden rounded-lg shadow-md mb-6">
                    {promotionalPolicyImageUrl ? (
                      <img 
                        src={promotionalPolicyImageUrl}
                        alt={aboutPageData.promotionalPolicyTitle || 'Student advancement'} 
                        className="absolute inset-0 w-full h-full object-fill"
                      />
                    ) : (
                      <div className="absolute inset-0 w-full h-full object-cover bg-gray-300"></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  </div>
                  
                  {/* Key Requirements - Use Sanity Data */}
                  <div className="bg-[#691e31] text-white p-6 rounded-lg">
                    <div className="bg-yellow-500 p-3 rounded-t-lg -mt-6 -mx-6 mb-6 px-6">
                      <h3 className="text-xl font-bold text-[#2a4865]">Key Requirements</h3>
                    </div>
                    <ul className="space-y-3">
                       {aboutPageData.promotionalKeyRequirements?.map((req, index) => (
                          <li key={index} className="flex items-start">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span>{req}</span>
                          </li>
                       ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Accountability/Performance Reports Tab */}
          {activeTab === 'accountability' && (
            <div>
              <div className="flex justify-between items-center mb-10">
                <h2 className="text-[#2a4865] text-3xl font-bold relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                  {aboutPageData.accountabilityTabTitle}
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white p-8 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold text-[#691e31] mb-4 relative overflow-hidden pb-2">
                    {aboutPageData.accountabilityTabTitle}
                    <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-yellow-500"></span>
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {aboutPageData.accountabilityIntroText}
                  </p>
                  
                  {/* Render links using Sanity labels AND URLs with added check */}
                  {aboutPageData.accountabilityReports?.map((report) => (
                     // Add a check to ensure report object exists before accessing properties
                     report && report.url && report.label && (
                       <a 
                        key={report._key} 
                        href={report.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center bg-[#691e31] text-white hover:bg-[#7f2a3e] py-3 px-6 rounded-full transition-colors mb-3 mr-3"
                      >
                        {report.label}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5z" />
                        </svg>
                      </a>
                     )
                  ))}
                  
                </div>
                
                {/* Keep static image/content block */}
                <div className="relative">
                  <div className="h-64 md:h-full bg-gray-200 rounded-lg overflow-hidden relative shadow-md">
                    <img 
                      src="/images/homepage/PS52-program4.jpg" 
                      alt="School performance" 
                      className="absolute inset-0 w-full h-full object-fill"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                      <div className="p-6 w-full">
                        <div className="w-12 h-1 bg-yellow-500 mb-6"></div>
                        <h3 className="text-white text-xl font-semibold mb-4">Key Performance Areas</h3>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg text-center">
                            <div className="text-white text-2xl font-bold">A</div>
                            <div className="text-white/80 text-xs">Student Achievement</div>
                          </div>
                          <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg text-center">
                            <div className="text-white text-2xl font-bold">B</div>
                            <div className="text-white/80 text-xs">Student Progress</div>
                          </div>
                          <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg text-center">
                            <div className="text-white text-2xl font-bold">A</div>
                            <div className="text-white/80 text-xs">School Environment</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AboutPage; 